import { FC, useState, useMemo } from 'react';
import { AppCard, AppPage } from '../../components/general';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  FeeMigrationDiff,
  FeeMigrationHudLineDiff,
  FeeMigrationConfigDiff,
  FeeMigrationConfigGroupDiff,
  FeeMigrationRuleDiff,
  FeeMigrationImportRequest,
  FeeMigrationImportResult,
  DiffAction,
} from '../../models/fees';

interface MigrationReviewProps {
  diff: FeeMigrationDiff;
  onImport: (request: FeeMigrationImportRequest) => Promise<FeeMigrationImportResult>;
  onCancel: () => void;
}

const actionSeverity = (action: DiffAction): 'success' | 'info' | 'danger' => {
  switch (action) {
    case DiffAction.Insert: return 'success';
    case DiffAction.Update: return 'info';
    case DiffAction.Delete: return 'danger';
    case DiffAction.Unchanged: return 'info';
  }
};

const actionIcon = (action: DiffAction): string => {
  switch (action) {
    case DiffAction.Insert: return 'pi pi-plus-circle';
    case DiffAction.Update: return 'pi pi-pencil';
    case DiffAction.Delete: return 'pi pi-trash';
    case DiffAction.Unchanged: return 'pi pi-minus';
  }
};

export const MigrationReview: FC<MigrationReviewProps> = ({ diff, onImport, onCancel }) => {
  const allHudLineKeys = useMemo(() => diff.hudLineDiffs.map((_, i) => i), [diff]);
  const allConfigKeys = useMemo(() => diff.configurationDiffs.map((_, i) => i), [diff]);
  const allGroupKeys = useMemo(() => diff.configGroupDiffs.map((_, i) => i), [diff]);

  const groupNameByGuid = useMemo(() => {
    const map = new Map<string, string>();
    diff.configGroupDiffs.forEach(({ imported, current }) => {
      const group = imported ?? current;
      if (group) map.set(group.feeConfigGroupGuid, group.name);
    });
    return map;
  }, [diff]);

  const [selectedHudLines, setSelectedHudLines] = useState<Set<number>>(new Set(allHudLineKeys));
  const [selectedConfigs, setSelectedConfigs] = useState<Set<number>>(new Set(allConfigKeys));
  const [selectedGroups, setSelectedGroups] = useState<Set<number>>(new Set(allGroupKeys));
  const [configFirst, setConfigFirst] = useState(0);
  const [groupFirst, setGroupFirst] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<FeeMigrationImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const totalSelected = selectedHudLines.size + selectedConfigs.size + selectedGroups.size;
  const totalDiffs = diff.hudLineDiffs.length + diff.configurationDiffs.length + diff.configGroupDiffs.length;

  const toggleHudLine = (index: number) => {
    setSelectedHudLines((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleConfig = (index: number) => {
    setSelectedConfigs((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleGroup = (index: number) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedHudLines(new Set(allHudLineKeys));
    setSelectedConfigs(new Set(allConfigKeys));
    setSelectedGroups(new Set(allGroupKeys));
  };

  const deselectAll = () => {
    setSelectedHudLines(new Set());
    setSelectedConfigs(new Set());
    setSelectedGroups(new Set());
  };

  const handleImport = async () => {
    setShowConfirmDialog(false);
    setIsImporting(true);
    setError(null);

    const request: FeeMigrationImportRequest = {
      hudLineDiffs: diff.hudLineDiffs.filter((_, i) => selectedHudLines.has(i)),
      configurationDiffs: diff.configurationDiffs.filter((_, i) => selectedConfigs.has(i)),
      configGroupDiffs: diff.configGroupDiffs.filter((_, i) => selectedGroups.has(i)),
    };

    try {
      const result = await onImport(request);
      setImportResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  // Summary counts
  const selectedInserts = {
    hudLines: diff.hudLineDiffs.filter((d, i) => selectedHudLines.has(i) && d.action === DiffAction.Insert).length,
    configs: diff.configurationDiffs.filter((d, i) => selectedConfigs.has(i) && d.action === DiffAction.Insert).length,
    groups: diff.configGroupDiffs.filter((d, i) => selectedGroups.has(i) && d.action === DiffAction.Insert).length,
  };
  const selectedUpdates = {
    hudLines: diff.hudLineDiffs.filter((d, i) => selectedHudLines.has(i) && d.action === DiffAction.Update).length,
    configs: diff.configurationDiffs.filter((d, i) => selectedConfigs.has(i) && d.action === DiffAction.Update).length,
    groups: diff.configGroupDiffs.filter((d, i) => selectedGroups.has(i) && d.action === DiffAction.Update).length,
  };
  const selectedDeletes = {
    hudLines: diff.hudLineDiffs.filter((d, i) => selectedHudLines.has(i) && d.action === DiffAction.Delete).length,
    configs: diff.configurationDiffs.filter((d, i) => selectedConfigs.has(i) && d.action === DiffAction.Delete).length,
    groups: diff.configGroupDiffs.filter((d, i) => selectedGroups.has(i) && d.action === DiffAction.Delete).length,
  };

  if (isImporting) {
    return (
      <AppPage
        title="Migration Review"
        breadcrumbs={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'Fees', to: '/products/fees' },
          { label: 'Migration Review' },
        ]}
      >
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-3 py-6">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span className="text-lg font-semibold">Importing changes...</span>
            <span className="text-sm text-gray-500">Please do not navigate away from this page.</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (importResult) {
    return (
      <AppPage
        title="Migration Review"
        breadcrumbs={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'Fees', to: '/products/fees' },
          { label: 'Migration Review' },
        ]}
      >
        <AppCard>
          <div className="flex flex-column gap-4">
            <div className={`p-4 border-round ${importResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex align-items-center gap-2 mb-3">
                <i className={`pi ${importResult.success ? 'pi-check-circle text-green-600' : 'pi-exclamation-triangle text-red-600'} text-2xl`} />
                <span className="text-lg font-bold">
                  {importResult.success ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                </span>
              </div>
              <div className="grid">
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">HUD Lines Inserted</div>
                  <div className="text-xl font-bold text-green-700">{importResult.hudLinesInserted}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">HUD Lines Updated</div>
                  <div className="text-xl font-bold text-blue-700">{importResult.hudLinesUpdated}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">HUD Lines Deleted</div>
                  <div className="text-xl font-bold text-red-700">{importResult.hudLinesDeleted}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">Configs Inserted</div>
                  <div className="text-xl font-bold text-green-700">{importResult.configurationsInserted}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">Configs Updated</div>
                  <div className="text-xl font-bold text-blue-700">{importResult.configurationsUpdated}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">Configs Deleted</div>
                  <div className="text-xl font-bold text-red-700">{importResult.configurationsDeleted}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">Groups Inserted</div>
                  <div className="text-xl font-bold text-green-700">{importResult.configGroupsInserted}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">Groups Updated</div>
                  <div className="text-xl font-bold text-blue-700">{importResult.configGroupsUpdated}</div>
                </div>
                <div className="col-6 md:col-4">
                  <div className="text-sm text-gray-600">Groups Deleted</div>
                  <div className="text-xl font-bold text-red-700">{importResult.configGroupsDeleted}</div>
                </div>
              </div>
              {importResult.errors.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-red-700 mb-1">Errors:</div>
                  {importResult.errors.map((err, i) => (
                    <div key={i} className="text-sm text-red-600">• {err}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button label="Back to Fees" icon="pi pi-arrow-left" onClick={onCancel} />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (totalDiffs === 0) {
    return (
      <AppPage
        title="Migration Review"
        breadcrumbs={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'Fees', to: '/products/fees' },
          { label: 'Migration Review' },
        ]}
      >
        <AppCard>
          <div className="flex flex-column align-items-center gap-3 py-6">
            <i className="pi pi-check-circle text-green-500 text-5xl" />
            <span className="text-lg font-semibold">No differences found</span>
            <span className="text-sm text-gray-500">The imported file matches the current draft configuration.</span>
            <Button label="Back to Fees" icon="pi pi-arrow-left" onClick={onCancel} className="mt-2" />
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Migration Review"
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Fees', to: '/products/fees' },
        { label: 'Migration Review' },
      ]}
      headerEnd={
        <div className="flex gap-2 align-items-center">
          <span className="text-sm text-gray-600">{totalSelected} of {totalDiffs} selected</span>
          <Button label="Select All" className="p-button-sm p-button-text" onClick={selectAll} />
          <Button label="Deselect All" className="p-button-sm p-button-text" onClick={deselectAll} />
          <Button
            label="Import Selected"
            icon="pi pi-upload"
            className="p-button-sm p-button-primary"
            onClick={() => setShowConfirmDialog(true)}
            disabled={totalSelected === 0}
          />
          <Button
            label="Cancel"
            className="p-button-sm p-button-secondary"
            onClick={onCancel}
          />
        </div>
      }
    >
      {error && (
        <div className="mb-3 p-3 bg-red-50 border-round">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Summary bar */}
      <div className="mb-3 p-3 surface-100 border-round flex gap-4 align-items-center flex-wrap">
        <span className="font-semibold text-sm">Summary:</span>
        <Tag severity="success" value={`${selectedInserts.hudLines + selectedInserts.configs + selectedInserts.groups} Insert(s)`} icon="pi pi-plus-circle" />
        <Tag severity="info" value={`${selectedUpdates.hudLines + selectedUpdates.configs + selectedUpdates.groups} Update(s)`} icon="pi pi-pencil" />
        <Tag severity="danger" value={`${selectedDeletes.hudLines + selectedDeletes.configs + selectedDeletes.groups} Delete(s)`} icon="pi pi-trash" />
      </div>

      {/* Config Group Diffs */}
      {diff.configGroupDiffs.length > 0 && (
        <AppCard>
          <h3 className="mt-0 mb-3">Config Group Changes ({diff.configGroupDiffs.length})</h3>
          <DataTable
            key={`grp-${selectedGroups.size}`}
            value={diff.configGroupDiffs.map((d, i) => ({ ...d, _index: i }))}
            stripedRows
            className="p-datatable-sm p-datatable-gridlines"
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            first={groupFirst}
            onPage={(e) => setGroupFirst(e.first)}
            emptyMessage="No config group changes"
          >
            <Column
              header=""
              style={{ width: '3rem' }}
              body={(row: FeeMigrationConfigGroupDiff & { _index: number }) => (
                <Checkbox
                  checked={selectedGroups.has(row._index)}
                  onChange={() => toggleGroup(row._index)}
                />
              )}
            />
            <Column
              field="action"
              header="Action"
              style={{ width: '8rem' }}
              body={(row: FeeMigrationConfigGroupDiff) => (
                <Tag severity={actionSeverity(row.action)} value={row.action} icon={actionIcon(row.action)} />
              )}
            />
            <Column field="name" header="Name" />
            <Column
              header="Parent Group"
              body={(row: FeeMigrationConfigGroupDiff) => {
                const group = row.imported ?? row.current;
                const parentGuid = group?.parentFeeConfigGroupGuid ?? null;
                if (!parentGuid) return <span className="text-sm text-gray-500">Root</span>;
                return <span className="text-sm">{groupNameByGuid.get(parentGuid) ?? parentGuid}</span>;
              }}
            />
            <Column
              header="Changed Fields"
              body={(row: FeeMigrationConfigGroupDiff) =>
                row.changedFields.length > 0
                  ? row.changedFields.map((f) => (
                      <Tag key={f} value={f} className="mr-1 mb-1" severity="warning" style={{ fontSize: '0.7rem' }} />
                    ))
                  : '-'
              }
            />
          </DataTable>
        </AppCard>
      )}

      {/* HUD Lines Diffs */}
      {diff.hudLineDiffs.length > 0 && (
        <AppCard>
          <h3 className="mt-0 mb-3">HUD Line Changes ({diff.hudLineDiffs.length})</h3>
          <DataTable
            key={`hud-${selectedHudLines.size}`}
            value={diff.hudLineDiffs.map((d, i) => ({ ...d, _index: i }))}
            stripedRows
            className="p-datatable-sm p-datatable-gridlines"
            emptyMessage="No HUD line changes"
          >
            <Column
              header=""
              style={{ width: '3rem' }}
              body={(row: FeeMigrationHudLineDiff & { _index: number }) => (
                <Checkbox
                  checked={selectedHudLines.has(row._index)}
                  onChange={() => toggleHudLine(row._index)}
                />
              )}
            />
            <Column
              field="action"
              header="Action"
              style={{ width: '8rem' }}
              body={(row: FeeMigrationHudLineDiff) => (
                <Tag severity={actionSeverity(row.action)} value={row.action} icon={actionIcon(row.action)} />
              )}
            />
            <Column field="hudLine" header="HUD Line" style={{ width: '8rem' }} />
            <Column
              header="Description"
              body={(row: FeeMigrationHudLineDiff) => {
                if (row.action === DiffAction.Insert) return row.imported?.description ?? '-';
                if (row.action === DiffAction.Delete) return row.current?.description ?? '-';
                return (
                  <div>
                    <div className="text-sm"><span className="font-semibold">Current:</span> {row.current?.description ?? '-'}</div>
                    <div className="text-sm"><span className="font-semibold">Imported:</span> {row.imported?.description ?? '-'}</div>
                  </div>
                );
              }}
            />
            <Column
              header="Changed Fields"
              body={(row: FeeMigrationHudLineDiff) =>
                row.changedFields.length > 0
                  ? row.changedFields.map((f) => (
                      <Tag key={f} value={f} className="mr-1 mb-1" severity="warning" style={{ fontSize: '0.7rem' }} />
                    ))
                  : '-'
              }
            />
          </DataTable>
        </AppCard>
      )}

      {/* Configuration Diffs */}
      {diff.configurationDiffs.length > 0 && (
        <AppCard>
          <h3 className="mt-0 mb-3">Configuration Changes ({diff.configurationDiffs.length})</h3>
          <DataTable
            key={`cfg-${selectedConfigs.size}`}
            value={diff.configurationDiffs.map((d, i) => ({ ...d, _index: i }))}
            stripedRows
            className="p-datatable-sm p-datatable-gridlines"
            paginator
            rows={10}
            first={configFirst}
            onPage={(e) => setConfigFirst(e.first)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            emptyMessage="No configuration changes"
          >
            <Column
              header=""
              style={{ width: '3rem' }}
              body={(row: FeeMigrationConfigDiff & { _index: number }) => (
                <Checkbox
                  checked={selectedConfigs.has(row._index)}
                  onChange={() => toggleConfig(row._index)}
                />
              )}
            />
            <Column
              field="action"
              header="Action"
              style={{ width: '8rem' }}
              body={(row: FeeMigrationConfigDiff) => (
                <Tag severity={actionSeverity(row.action)} value={row.action} icon={actionIcon(row.action)} />
              )}
            />
            <Column field="hudLine" header="HUD Line" style={{ width: '8rem' }} />
            <Column
              header="Description"
              body={(row: FeeMigrationConfigDiff) => {
                if (row.action === DiffAction.Insert) return row.imported?.description ?? '-';
                if (row.action === DiffAction.Delete) return row.current?.description ?? '-';
                return (
                  <div>
                    <div className="text-sm"><span className="font-semibold">Current:</span> {row.current?.description ?? '-'}</div>
                    <div className="text-sm"><span className="font-semibold">Imported:</span> {row.imported?.description ?? '-'}</div>
                  </div>
                );
              }}
            />
            <Column
              header="Rules"
              body={(row: FeeMigrationConfigDiff) => {
                if (row.ruleDiffs && row.ruleDiffs.length > 0) {
                  return (
                    <div className="flex flex-column gap-1">
                      {row.ruleDiffs.map((rd: FeeMigrationRuleDiff) => (
                        <div key={rd.ruleId} className="flex align-items-center gap-1">
                          <Tag
                            severity={actionSeverity(rd.action)}
                            value={rd.action}
                            icon={actionIcon(rd.action)}
                            style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem' }}
                          />
                          <span className="text-sm">{rd.description || rd.ruleId}</span>
                          {rd.changedFields.length > 0 && (
                            <span className="text-xs text-gray-500">({rd.changedFields.join(', ')})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }
                const count = row.imported?.rules?.length ?? row.current?.rules?.length ?? 0;
                return <span className="text-sm">{count} rule(s)</span>;
              }}
            />
            <Column
              header="Changed Fields"
              body={(row: FeeMigrationConfigDiff) =>
                row.changedFields.length > 0
                  ? row.changedFields.map((f) => (
                      <Tag key={f} value={f} className="mr-1 mb-1" severity="warning" style={{ fontSize: '0.7rem' }} />
                    ))
                  : '-'
              }
            />
          </DataTable>
        </AppCard>
      )}

      {/* Confirm Dialog */}
      <Dialog
        header="Confirm Import"
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        style={{ width: '480px' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              className="p-button-secondary"
              onClick={() => setShowConfirmDialog(false)}
            />
            <Button
              label="Yes, Import"
              icon="pi pi-upload"
              className="p-button-danger"
              onClick={handleImport}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div className="bg-yellow-50 p-3 border-round">
            <div className="flex align-items-start gap-2">
              <i className="pi pi-exclamation-triangle text-yellow-600 text-xl mt-1" />
              <div>
                <p className="m-0 font-semibold text-yellow-800">This will override the current draft.</p>
                <p className="m-0 mt-1 text-sm text-yellow-700">
                  The following changes will be applied to your draft fee configurations:
                </p>
              </div>
            </div>
          </div>
          <div className="surface-100 p-3 border-round">
            <div className="grid">
              <div className="col-6">
                <div className="text-sm font-semibold mb-1">HUD Lines</div>
                {selectedInserts.hudLines > 0 && <div className="text-sm text-green-700">+ {selectedInserts.hudLines} insert(s)</div>}
                {selectedUpdates.hudLines > 0 && <div className="text-sm text-blue-700">~ {selectedUpdates.hudLines} update(s)</div>}
                {selectedDeletes.hudLines > 0 && <div className="text-sm text-red-700">- {selectedDeletes.hudLines} delete(s)</div>}
                {selectedInserts.hudLines === 0 && selectedUpdates.hudLines === 0 && selectedDeletes.hudLines === 0 && (
                  <div className="text-sm text-gray-500">No changes</div>
                )}
              </div>
              <div className="col-6">
                <div className="text-sm font-semibold mb-1">Configurations</div>
                {selectedInserts.configs > 0 && <div className="text-sm text-green-700">+ {selectedInserts.configs} insert(s)</div>}
                {selectedUpdates.configs > 0 && <div className="text-sm text-blue-700">~ {selectedUpdates.configs} update(s)</div>}
                {selectedDeletes.configs > 0 && <div className="text-sm text-red-700">- {selectedDeletes.configs} delete(s)</div>}
                {selectedInserts.configs === 0 && selectedUpdates.configs === 0 && selectedDeletes.configs === 0 && (
                  <div className="text-sm text-gray-500">No changes</div>
                )}
              </div>
              <div className="col-6 mt-2">
                <div className="text-sm font-semibold mb-1">Config Groups</div>
                {selectedInserts.groups > 0 && <div className="text-sm text-green-700">+ {selectedInserts.groups} insert(s)</div>}
                {selectedUpdates.groups > 0 && <div className="text-sm text-blue-700">~ {selectedUpdates.groups} update(s)</div>}
                {selectedDeletes.groups > 0 && <div className="text-sm text-red-700">- {selectedDeletes.groups} delete(s)</div>}
                {selectedInserts.groups === 0 && selectedUpdates.groups === 0 && selectedDeletes.groups === 0 && (
                  <div className="text-sm text-gray-500">No changes</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </AppPage>
  );
};
